import { useEffect, useState } from 'react';
import { Icon } from './Icon';
import { useGameStore, type OpenModal } from '@/ui/store/gameStore';
import { eventBus } from '@/shared/EventBus';
import { resolveAlbumEvent } from '@/config/events.config';
import { avatarAssetUrl } from '@/assets/paths';

/**
 * ActionMenu — Floating Action Button + collapsible vertical stack (sesja 30).
 *
 * Zastępuje 4 zawsze widoczne `<ActionButton>` w App.tsx (Pety / Jajka / Ulepsz.
 * / Mapa). Mobile-friendly pattern:
 *
 *  - Default: 1 button (chevron `<`) w prawym dolnym rogu — kompaktowy
 *  - Klik FAB: stos opcji rozwija się pionowo W GÓRĘ od FAB-a (cascade animation)
 *  - Klik na akcję: otwiera modal + zwija menu automatycznie
 *  - Outside click: zwija menu (invisible overlay z `onClick`)
 *  - Drugi klik FAB: zwija menu (ikona toggle chevron `<` ↔ close `×`)
 *
 * **Dwie strony (przebudowa — 9 pozycji robiło za długi stos pod kciuk):**
 *   Strona 1 „główne" (od dołu, najbliżej kciuka): Pety, Jajka, Mapa, Ranking,
 *     Plac, Konto.
 *   Strona 2 „rzadsze": Ustawienia, Mikstury (plecak), Ulepszenia, Osiągnięcia.
 *   Pigułka na górze stosu („Więcej" ↔ „Wstecz") przełącza strony. Zwinięcie
 *   menu resetuje na stronę 1 (kolejne otwarcie startuje od głównych).
 *
 * **Animacja stagger:**
 *  - 40ms cascade per button przy expanding (6 × 40ms = 240ms total)
 *  - Każdy button: slide-up + fade-in z translate-y-2 → 0
 *  - Przy collapsing: synchroniczne fade-out (krótsze, gracz chce szybko zamknąć)
 *
 * **A11y:**
 *  - FAB: `aria-expanded`, `aria-haspopup="menu"`, `aria-label`
 *  - Stack: `role="menu"`, każdy button `role="menuitem"`
 *
 * **Auto-collapse triggers:**
 *  - openModal !== null (gracz otworzył jakieś menu)
 *  - hatchPhase === 'animating' (cinematic chowa cały HUD przez Wariant A)
 */

interface MenuOption {
  modal: OpenModal;
  iconSrc: string;
  iconFallback: string;
  label: string;
  /** Zamiast otwierać modal — wejdź na scenę plazy (5.B.3, emit 'plaza:enter'). */
  enterPlaza?: boolean;
}

// Menu DWUSTRONICOWE (przebudowa: 9 pozycji robiło za długi stos pod kciuk).
// Kolejność w tablicy = od GÓRY stosu (idx 0, najwyżej od FAB) do DOŁU (najbliżej
// FAB, czyli najwygodniej kciukiem). Najczęstsze akcje na dole.
//
// 5.B.9 (decyzja usera): handel = WYŁĄCZNIE plac. „Zagroda" (myStall) NIE jest w
// FAB — zarządzanie zagrodą/zakup/sprzedaż tylko przez dotknięcie budki/NPC na
// placu. FAB ma tylko „Plac" jako wejście do handlu.

// Strona 1 — „gdzie idę / czym zarządzam" (codzienne, częste).
const MENU_PAGE_1: readonly MenuOption[] = [
  { modal: 'account',     iconSrc: '/ui/ui_btn_account.webp',     iconFallback: '👤', label: 'Konto' },
  { modal: null, enterPlaza: true, iconSrc: '/ui/ui_btn_teleport.webp', iconFallback: '🌀', label: 'Plac' },
  { modal: 'leaderboard', iconSrc: '/ui/ui_btn_leaderboard.webp', iconFallback: '🏆', label: 'Ranking' },
  { modal: 'map',         iconSrc: '/ui/ui_btn_map.webp',         iconFallback: '🗺️', label: 'Mapa' },
  { modal: 'eggs',        iconSrc: '/ui/ui_btn_eggs.webp',        iconFallback: '🥚', label: 'Jajka' },
  { modal: 'pets',        iconSrc: '/ui/ui_btn_pets.webp',        iconFallback: '🐾', label: 'Pety' },
] as const;

// Strona 2 — rzadsze opcje (za pigułką „Więcej").
const MENU_PAGE_2: readonly MenuOption[] = [
  { modal: 'settings',     iconSrc: '/ui/ui_btn_settings.webp',   iconFallback: '⚙️', label: 'Ustaw.' },
  { modal: 'achievements', iconSrc: '/ui/ui_btn_achievement.webp', iconFallback: '🏅', label: 'Osiągn.' },
  { modal: 'upgrades',     iconSrc: '/ui/ui_btn_upgrades.webp',    iconFallback: '⬆️', label: 'Ulepsz.' },
  { modal: 'potionBag',    iconSrc: '/ui/ui_potion_combo.webp',    iconFallback: '🧪', label: 'Mikstury' },
] as const;

const STAGGER_MS = 40;

export function ActionMenu(): JSX.Element {
  const [expanded, setExpanded] = useState(false);
  // Menu dwustronicowe: 1 = główne (Pety/Jajka/Mapa/Ranking/Plac/Konto),
  // 2 = rozwój (Osiągn./Ulepsz./Mikstury). Pigułka na górze stosu przełącza.
  const [page, setPage] = useState<1 | 2>(1);
  const setOpenModal = useGameStore((s) => s.setOpenModal);
  const openModal = useGameStore((s) => s.openModal);
  const activeScene = useGameStore((s) => s.activeScene);
  const events = useGameStore((s) => s.events);
  const avatarSkinId = useGameStore((s) => s.avatarSkinId);
  const setHasSeenFabTutorial = useGameStore((s) => s.setHasSeenFabTutorial);
  const hasSeenFabTutorial = useGameStore((s) => s.hasSeenFabTutorial);
  // Nagroda „Pełny album" czeka na odbiór → czerwona kropka prowadzi dziecko
  // do „Osiągn." (FAB zwinięty → kropka na FAB; strona 1 → na „Więcej").
  const hasClaimableReward = useGameStore((s) => s.claimableAchievementIds.length > 0);

  // 5.B.3 (Codex MED 1): „Plac" (enterPlaza) ma sens tylko w świecie. Gdy gracz
  // jest już w plazie, WorldScene jest zatrzymana i jej listener `plaza:enter`
  // został sprzątnięty — klik emitowałby martwy event. Filtrujemy tę opcję poza
  // światem (wyjście z placu obsługuje osobny przycisk „Wróć z placu" w App).
  // Strona 2 (rozwój) + stałe pozycje customizacji:
  //  - „Wygląd" — ZAWSZE (zmiana awatara z odblokowanych skinów; ikona = bieżący avatar).
  //  - „Album" — gdy config ma event z albumem (resolveAlbumEvent zwraca go też poza
  //    oknem eventu = kolekcja stała + odbiór kompletu po evencie).
  const albumEvent = resolveAlbumEvent(events);
  const page2: readonly MenuOption[] = [
    ...MENU_PAGE_2,
    { modal: 'avatarCustomize', iconSrc: avatarAssetUrl(avatarSkinId), iconFallback: '🙂', label: 'Wygląd' },
    ...(albumEvent
      ? [{ modal: 'eventAlbum' as const, iconSrc: albumEvent.currency?.iconKey ?? '/ui/ui_shell.webp', iconFallback: albumEvent.currency?.fallbackEmoji ?? '🐚', label: 'Album' }]
      : []),
  ];

  const options = (page === 1 ? MENU_PAGE_1 : page2).filter(
    (o) => !o.enterPlaza || activeScene === 'world',
  );

  // Auto-collapse gdy modal się otworzy (uniknięcie sytuacji "menu rozwinięte
  // pod modalem"). openModal staje się non-null po klik MenuOption, ale też
  // mogłoby być triggered externally (np. dev helper / event).
  useEffect(() => {
    if (openModal !== null && expanded) {
      setExpanded(false);
    }
  }, [openModal, expanded]);

  // Zwinięcie menu (klik akcji / poza / FAB) zawsze wraca na stronę 1, żeby
  // kolejne otwarcie startowało od głównych opcji (nie zostawało na „rozwoju").
  useEffect(() => {
    if (!expanded) setPage(1);
  }, [expanded]);

  const handlePageToggle = (): void => setPage((p) => (p === 1 ? 2 : 1));

  const handleToggle = (): void => {
    setExpanded((prev) => !prev);
    // Sesja 30: pierwsze kliknięcie FAB oznacza że gracz "odkrył" menu —
    // chowamy tutorial arrow. FabTutorialArrow ma własny useEffect listenujący
    // na ten flag (przez Zustand), ale tu też persistujemy do save'a żeby
    // tutorial nie pokazał się znowu po reload.
    if (!hasSeenFabTutorial) {
      setHasSeenFabTutorial(true);
      void persistTutorialFlag();
    }
  };

  const handleSelect = (opt: MenuOption): void => {
    if (opt.enterPlaza) {
      eventBus.emit('plaza:enter', {});
    } else {
      setOpenModal(opt.modal);
    }
    setExpanded(false);
  };

  return (
    <>
      {/* Outside-click overlay — invisible, łapie kliki poza FAB i stosem
          żeby zwinąć menu. z-30 nad gameplay, pod modal'ami (z-40) i hatch
          cinematic (z-50). */}
      {expanded && (
        <div
          className="fixed inset-0 z-30 pointer-events-auto"
          onClick={() => setExpanded(false)}
          aria-hidden="true"
        />
      )}

      {/* Stack przycisków — absolute positioned nad FAB-em. Pierwsze w array są
          najwyżej, ostatnie (Pety) najbliżej kciuka przy FAB; pigułka stron na samej
          górze stosu. */}
      <div
        className="absolute bottom-3 right-3 z-40 flex flex-col items-end gap-2 pointer-events-auto"
        role="menu"
        aria-orientation="vertical"
      >
        {/* Renderujemy stack TYLKO gdy expanded — żeby nie blokował kliku
            na rzeczy pod spodem gdy collapsed. Pierwszy w array (na stronie 1: Konto)
            jest najwyżej, ostatni (Pety) najbliżej FAB. */}
        {/* Pigułka przełączania stron — na górze stosu, SAMA strzałka (bez tekstu).
            Strona 1: chevron w lewo („więcej" → strona 2). Strona 2: TEN SAM chevron
            odbity w poziomie (-scale-x-100) → wskazuje w prawo („wstecz" → strona 1).
            Okrągły kształt odróżnia ją od kart-akcji (rounded-2xl). NIE woła
            handleSelect — tylko przełącza stronę, menu zostaje rozwinięte. */}
        {expanded && (
          <button
            type="button"
            role="menuitem"
            onClick={handlePageToggle}
            aria-label={page === 1 ? 'Pokaż więcej opcji' : 'Wróć do głównych opcji'}
            className="relative bg-white/95 hover:bg-white active:scale-95 rounded-full shadow-lg p-2.5 flex items-center justify-center transition-all"
            style={{ animation: 'actionMenuItemEnter 180ms ease-out backwards' }}
          >
            <Icon
              src="/ui/ui_btn_menu_chevron.webp"
              fallback={page === 1 ? '‹' : '›'}
              alt={page === 1 ? 'więcej' : 'wstecz'}
              className={`w-6 h-6 ${page === 2 ? '-scale-x-100' : ''}`}
            />
            {page === 1 && hasClaimableReward && <RewardDot />}
          </button>
        )}

        {expanded && options.map((opt, idx) => (
          <button
            key={opt.label}
            type="button"
            role="menuitem"
            onClick={() => handleSelect(opt)}
            className="relative bg-white/95 hover:bg-white active:scale-95 rounded-2xl shadow-lg px-3 py-2 flex flex-col items-center min-w-[68px] transition-all"
            style={{
              // Cascade: pigułka (delay 0) pierwsza, potem opcje od góry do dołu
              // (idx+1, żeby pigułka prowadziła). Naturalne „rozwijanie" w dół.
              animation: `actionMenuItemEnter 180ms ease-out ${(idx + 1) * STAGGER_MS}ms backwards`,
            }}
          >
            <Icon src={opt.iconSrc} fallback={opt.iconFallback} alt={opt.label} className="w-8 h-8" />
            <span className="mt-0.5 text-xs font-bold text-meadow-700">{opt.label}</span>
            {opt.modal === 'achievements' && hasClaimableReward && <RewardDot />}
          </button>
        ))}

        {/* FAB — zawsze widoczny */}
        <button
          type="button"
          onClick={handleToggle}
          aria-expanded={expanded}
          aria-haspopup="menu"
          aria-label={expanded ? 'Zamknij menu' : 'Otwórz menu'}
          className="relative bg-white/95 hover:bg-white active:scale-95 rounded-full shadow-xl w-14 h-14 flex items-center justify-center transition-transform"
          style={{
            // Drobny rotation FAB przy expand — wizualny hint że stan się zmienił.
            transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 200ms ease-out',
          }}
        >
          <Icon
            src={expanded ? '/ui/ui_btn_close.webp' : '/ui/ui_btn_menu_chevron.webp'}
            fallback={expanded ? '✕' : '<'}
            alt={expanded ? 'zamknij' : 'menu'}
            className="w-8 h-8"
          />
          {!expanded && hasClaimableReward && <RewardDot />}
        </button>
      </div>

      {/* CSS keyframes inline — sesja 30: prosta cascade animation. Bez
          tworzenia osobnego pliku CSS bo dotyczy tylko tego komponentu. */}
      <style>{`
        @keyframes actionMenuItemEnter {
          from {
            opacity: 0;
            transform: translateY(8px) scale(0.92);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
      `}</style>
    </>
  );
}

/**
 * Czerwona kropka „nowa nagroda do odebrania" (nagroda „Pełny album"). Czysty
 * CSS — bez emoji w UI. Rodzic musi mieć `relative`.
 */
function RewardDot(): JSX.Element {
  return (
    <span
      className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-red-500 border-2 border-white shadow motion-safe:animate-pulse"
      role="status"
      aria-label="nowa nagroda do odebrania"
    />
  );
}

/**
 * Persistuje `flags.hasSeenFabTutorial=true` do save'a. Idempotent —
 * wielokrotne wywołanie OK (atomic recipe sprawdza i nic nie zmienia jeśli
 * już true).
 */
async function persistTutorialFlag(): Promise<void> {
  const { saveManager } = await import('@/save');
  await saveManager.update((current) => {
    if (current.flags.hasSeenFabTutorial === true) return current;
    return {
      ...current,
      flags: { ...current.flags, hasSeenFabTutorial: true },
    };
  });
}
